import { slugify } from '@sola/utils/string.ts';

/**
 * Well-known Skippy paths, for building links in code rather than content.
 *
 * Three places describe this same route surface and must agree:
 * - here, for code-built links
 * - `appRoutePaths` in the `libs/multisite` site definitions, for which paths
 *   authors may link to without a matching Contentful entry
 * - `RESERVED_URL_PATTERNS` in `@sola/contentful-model/lib/constants.ts`, for
 *   which paths authors may not create a page at
 *
 * Adding a route here usually means updating the other two.
 */
export const knownSkippyUrls = {
  homepage: '/',
  sac: '/sponsor-a-child',
  childBio: '/sponsor-a-child/child-bio',
  childBioNotFound: '/sponsor-a-child/404-gotten',
  blog: '/blog',
  crisisUpdates: '/crisis-updates',
  partner: '/partner',
  pressReleases: '/press/press-releases',
  search: '/search',
  give: '/give',
  volunteers: '/get-involved/volunteer/events',
};

/**
 * @TODO Reads `process.env` directly rather than going through
 * `getPublicConfig()`, so a missing value yields the string "undefined" in a URL
 * instead of failing validation. Also assumes the base URL has no trailing
 * slash.
 */
export const absoluteUrlFromPath = (path: string) => {
  return `${process.env.NEXT_PUBLIC_BASE_URL_DOTCOM}${path}`;
};

/**
 * Generates the path for a volunteer tour page (role with multiple locations).
 * @param tourName - The volunteer tour name or an existing tour slug
 */
export const getVolunteerTourPath = (tourName: string): string => {
  if (!tourName) {
    return '';
  }

  const tourSlug = slugify(tourName);

  return `${knownSkippyUrls.volunteers}/${tourSlug}`;
};

/**
 * Generates the path for a specific volunteer event page.
 * @param tourName - The volunteer tour name or an existing tour slug
 * @param eventId - The event/location identifier
 */
export const getVolunteerEventPath = (
  tourName?: string | null,
  eventId?: string | null,
): string => {
  if (!tourName || !eventId) {
    return '';
  }
  return `${getVolunteerTourPath(tourName)}/${eventId}`;
};
